export interface LongwallsDesignTheme {
  id: string;
  background: string;
  canvas: string;
  panel: string;
  panelAlt: string;
  ink: string;
  muted: string;
  line: string;
  accent: string;
  accentInk: string;
  accentSoft: string;
  shadow: string;
  radius: string;
  typeface: string;
  heroTexture: string;
  workspaceTexture: string;
}

const LONGWALLS_THEME_ALIAS_MAP: Record<string, string> = {
  "field-operations": "operational-field-app",
  "case-approval-desk": "approval-case-portal",
  "operations-cockpit": "operational-field-app",
  "generic-enterprise": "document-and-project-desk",
};

export const LONGWALLS_DESIGN_THEMES: Record<string, LongwallsDesignTheme> = {
  "finance-workflow": {
    id: "finance-workflow",
    background: "#f6f1e7",
    canvas: "linear-gradient(180deg, #f3ecdf 0%, #f8f3ea 48%, #f1ebdf 100%)",
    panel: "rgba(255,255,255,0.9)",
    panelAlt: "rgba(247, 243, 235, 0.92)",
    ink: "#13211b",
    muted: "#59675f",
    line: "rgba(19,33,27,0.12)",
    accent: "#0f6a55",
    accentInk: "#f5fbf7",
    accentSoft: "rgba(15,106,85,0.12)",
    shadow: "0 24px 60px rgba(19,33,27,0.08)",
    radius: "24px",
    typeface: "Georgia, \"Times New Roman\", serif",
    heroTexture: "radial-gradient(circle at top left, rgba(15,106,85,0.18), transparent 34%), radial-gradient(circle at bottom right, rgba(169,117,58,0.14), transparent 30%)",
    workspaceTexture:
      "linear-gradient(135deg, rgba(255,255,255,0.55), rgba(255,255,255,0.15)), repeating-linear-gradient(90deg, rgba(19,33,27,0.03) 0, rgba(19,33,27,0.03) 1px, transparent 1px, transparent 18px)",
  },
  "industrial-operations": {
    id: "industrial-operations",
    background: "#ecf0ec",
    canvas: "linear-gradient(180deg, #e7ece7 0%, #eef2ee 42%, #e5ece8 100%)",
    panel: "rgba(255,255,255,0.93)",
    panelAlt: "rgba(240, 244, 241, 0.92)",
    ink: "#18211c",
    muted: "#5b695e",
    line: "rgba(24,33,28,0.1)",
    accent: "#38654b",
    accentInk: "#f3faf5",
    accentSoft: "rgba(56,101,75,0.12)",
    shadow: "0 24px 60px rgba(24,33,28,0.08)",
    radius: "24px",
    typeface: "\"Aptos\", \"Segoe UI\", sans-serif",
    heroTexture:
      "linear-gradient(125deg, rgba(56,101,75,0.14), transparent 45%), radial-gradient(circle at bottom right, rgba(140,110,74,0.16), transparent 34%)",
    workspaceTexture:
      "linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0)), repeating-linear-gradient(0deg, rgba(24,33,28,0.03) 0, rgba(24,33,28,0.03) 1px, transparent 1px, transparent 20px)",
  },
  "healthcare-crm-desk": {
    id: "healthcare-crm-desk",
    background: "#f3f6f5",
    canvas: "linear-gradient(180deg, #f4f8f7 0%, #eef5f3 44%, #f7fbfa 100%)",
    panel: "rgba(255,255,255,0.95)",
    panelAlt: "rgba(240, 248, 246, 0.92)",
    ink: "#16302b",
    muted: "#5a6d67",
    line: "rgba(22,48,43,0.1)",
    accent: "#0f8a73",
    accentInk: "#f2fffb",
    accentSoft: "rgba(15,138,115,0.12)",
    shadow: "0 24px 60px rgba(22,48,43,0.08)",
    radius: "26px",
    typeface: "\"Aptos\", \"Segoe UI\", sans-serif",
    heroTexture:
      "radial-gradient(circle at top left, rgba(15,138,115,0.16), transparent 34%), radial-gradient(circle at bottom right, rgba(102,180,156,0.16), transparent 30%)",
    workspaceTexture:
      "linear-gradient(135deg, rgba(255,255,255,0.35), rgba(255,255,255,0.05)), radial-gradient(circle at right top, rgba(15,138,115,0.08), transparent 32%)",
  },
  "operational-field-app": {
    id: "operational-field-app",
    background: "#eef4f7",
    canvas: "linear-gradient(180deg, #ebf3f7 0%, #f3f8fb 42%, #e9f1f6 100%)",
    panel: "rgba(255,255,255,0.92)",
    panelAlt: "rgba(238, 246, 250, 0.92)",
    ink: "#102331",
    muted: "#556776",
    line: "rgba(16,35,49,0.1)",
    accent: "#005f8f",
    accentInk: "#f4fbff",
    accentSoft: "rgba(0,95,143,0.12)",
    shadow: "0 24px 60px rgba(16,35,49,0.08)",
    radius: "26px",
    typeface: "\"Segoe UI\", system-ui, sans-serif",
    heroTexture: "radial-gradient(circle at top right, rgba(0,95,143,0.18), transparent 32%), radial-gradient(circle at bottom left, rgba(23,153,112,0.14), transparent 28%)",
    workspaceTexture:
      "linear-gradient(135deg, rgba(255,255,255,0.25), rgba(255,255,255,0.02)), linear-gradient(90deg, rgba(16,35,49,0.03), transparent 35%)",
  },
  "approval-case-portal": {
    id: "approval-case-portal",
    background: "#f2f3f7",
    canvas: "linear-gradient(180deg, #eff1f7 0%, #f6f7fb 45%, #eef0f6 100%)",
    panel: "rgba(255,255,255,0.95)",
    panelAlt: "rgba(243, 244, 250, 0.93)",
    ink: "#1c2232",
    muted: "#61687b",
    line: "rgba(28,34,50,0.1)",
    accent: "#5b4bff",
    accentInk: "#f7f5ff",
    accentSoft: "rgba(91,75,255,0.1)",
    shadow: "0 22px 54px rgba(28,34,50,0.1)",
    radius: "22px",
    typeface: "\"Trebuchet MS\", \"Segoe UI\", sans-serif",
    heroTexture: "radial-gradient(circle at top left, rgba(91,75,255,0.16), transparent 34%), radial-gradient(circle at bottom right, rgba(255,122,89,0.12), transparent 28%)",
    workspaceTexture:
      "linear-gradient(180deg, rgba(255,255,255,0.15), rgba(255,255,255,0)), radial-gradient(circle at top left, rgba(91,75,255,0.08), transparent 28%)",
  },
  "dispatch-and-terrain-ops": {
    id: "dispatch-and-terrain-ops",
    background: "#eef5f1",
    canvas: "linear-gradient(180deg, #e9f2ed 0%, #f3f8f5 44%, #ebf4ef 100%)",
    panel: "rgba(255,255,255,0.93)",
    panelAlt: "rgba(238, 247, 242, 0.92)",
    ink: "#13312a",
    muted: "#60736b",
    line: "rgba(19,49,42,0.1)",
    accent: "#187c62",
    accentInk: "#f3fff8",
    accentSoft: "rgba(24,124,98,0.12)",
    shadow: "0 24px 60px rgba(19,49,42,0.08)",
    radius: "28px",
    typeface: "\"Segoe UI\", system-ui, sans-serif",
    heroTexture:
      "linear-gradient(135deg, rgba(24,124,98,0.16), transparent 40%), radial-gradient(circle at bottom left, rgba(93,146,126,0.16), transparent 30%)",
    workspaceTexture:
      "linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.04)), radial-gradient(circle at right center, rgba(24,124,98,0.1), transparent 32%)",
  },
  "document-and-project-desk": {
    id: "document-and-project-desk",
    background: "#f5f0ea",
    canvas: "linear-gradient(180deg, #f4eee7 0%, #fbf7f1 46%, #f1ebe2 100%)",
    panel: "rgba(255,255,255,0.94)",
    panelAlt: "rgba(247, 242, 236, 0.94)",
    ink: "#2c241e",
    muted: "#72655b",
    line: "rgba(44,36,30,0.1)",
    accent: "#8b5e3c",
    accentInk: "#fff8f2",
    accentSoft: "rgba(139,94,60,0.12)",
    shadow: "0 24px 60px rgba(44,36,30,0.08)",
    radius: "24px",
    typeface: "\"Georgia\", \"Times New Roman\", serif",
    heroTexture:
      "radial-gradient(circle at top right, rgba(139,94,60,0.16), transparent 34%), linear-gradient(145deg, rgba(194,155,118,0.12), transparent 45%)",
    workspaceTexture:
      "linear-gradient(135deg, rgba(255,255,255,0.25), rgba(255,255,255,0.06)), repeating-linear-gradient(0deg, rgba(139,94,60,0.03) 0, rgba(139,94,60,0.03) 1px, transparent 1px, transparent 22px)",
  },
};

function normalizeLongwallsThemeId(archetypeId: string): string {
  const normalized = archetypeId.trim();
  if (!normalized) return "document-and-project-desk";
  return LONGWALLS_THEME_ALIAS_MAP[normalized] ?? normalized;
}

export function getLongwallsDesignTheme(archetypeId: string): LongwallsDesignTheme {
  return LONGWALLS_DESIGN_THEMES[normalizeLongwallsThemeId(archetypeId)] ?? LONGWALLS_DESIGN_THEMES["document-and-project-desk"];
}

export function buildLongwallsThemeCss(archetypeId: string): string {
  const theme = getLongwallsDesignTheme(archetypeId);
  return `
  --bg: ${theme.background};
  --canvas: ${theme.canvas};
  --panel: ${theme.panel};
  --panel-alt: ${theme.panelAlt};
  --ink: ${theme.ink};
  --muted: ${theme.muted};
  --line: ${theme.line};
  --accent: ${theme.accent};
  --accent-ink: ${theme.accentInk};
  --accent-soft: ${theme.accentSoft};
  --shadow: ${theme.shadow};
  --radius: ${theme.radius};
  --typeface: ${theme.typeface};
  --hero-texture: ${theme.heroTexture};
  --workspace-texture: ${theme.workspaceTexture};
`;
}
